import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

interface VolumeVarianceConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Loud: -3 dBFS, Soft: -30 dBFS",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Loud: -6 dBFS, Soft: -35 dBFS",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Loud: -9 dBFS, Soft: -40 dBFS",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const VolumeVarianceConfig: React.FC<VolumeVarianceConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const currentPreset =
    metricRecordData.metric_metadata?.threshold_preset || "normal"
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold_preset: value as "strict" | "normal" | "lenient" | "custom",
      },
    })
  }

  const handleLoudThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        loud_threshold_db: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleSoftThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        soft_threshold_db: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        interval_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Threshold Preset</label>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <>
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Loud Threshold (dB)</label>
            </div>
            <input
              type="number"
              step="1"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.loud_threshold_db ?? ""}
              onChange={handleLoudThresholdChange}
              placeholder="-6"
              aria-label="Loud Threshold in decibels"
            />
            <p className="text-xs text-coval-text-muted">
              Audio above this level is considered too loud.
            </p>
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Soft Threshold (dB)</label>
            </div>
            <input
              type="number"
              step="1"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.soft_threshold_db ?? ""}
              onChange={handleSoftThresholdChange}
              placeholder="-35"
              aria-label="Soft Threshold in decibels"
            />
            <p className="text-xs text-coval-text-muted">
              Audio below this level is considered too soft.
            </p>
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>
                Analysis Interval (seconds)
              </label>
            </div>
            <input
              type="number"
              step="0.1"
              min="0.1"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.interval_seconds ?? ""}
              onChange={handleIntervalChange}
              placeholder="0.5"
              aria-label="Analysis Interval in seconds"
            />
            <p className="text-xs text-coval-text-muted">
              Duration of each audio segment to analyze.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
